import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { forDesktop, forIE } from '@westwing/ui-kit/breakPoints';
import { Direction } from '@westwing/ui-kit/Icons/StyledIcons';
import { body2, header1 } from '@westwing/ui-kit/typography';
import useSelectProductSize, { SizeOptions } from '../../ProductDetailPage/hooks/useSelectProductSize';
import Header, { TitlePosition } from '../GenericFlyout/Header';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { BellaTestIds } from 'Common/qaIds';
import { useScrollLockRef } from 'Client/components/Overlays/ScrollableArea';
import PriceRenderer from 'Client/components/Product/PriceRenderer';
import { useBellaSelector } from 'Client/redux/hooks';
import usePdpUrlState, { convertPdpStateToUrl } from 'Client/components/ProductDetailPage/hooks/pdpUrlState';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';

const SIZE_ITEM_HEIGHT = 77;
const SIZE_OVERLAY_HEADER_HEIGHT = 75;

const StyledPriceContainer = styled(PriceRenderer)`
    grid-area: price;
    text-align: right;

    ${forIE`
        display: block;
        -ms-grid-row: 2;
        -ms-grid-row-span: 2;
        -ms-grid-column: 3;
    `}
`;

const StyledHeader = styled(Header)`
    height: 75px;
    padding: 0 20px;

    > * {
        ${header1}
    }

    ${forDesktop`
        padding: 0 40px;
        height: 90px;
    `}
`;

const SizeItem = styled.div<{ disabled?: boolean }>`
    height: 100%;
    width: 100%;
    display: grid;
    margin: 0 20px;
    grid-template:
        '.        .     .     ' 1fr
        'label    .     price ' 20px
        'label    .     price ' auto
        '.        .     .     ' 1fr
        / 1fr 20px 49px;
    color: ${({ disabled, theme }) => (disabled ? theme.colors.charcoal20 : theme.colors.charcoal100)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.charcoal20};

    ${forDesktop`
        margin: 0 40px;
    `}

    ${forIE`
        display: -ms-grid;
        -ms-grid-columns:  1fr 20px 49px;
        -ms-grid-rows:  1fr 20px auto 1fr;
    `}
`;

const StyledSizeNameContainer = styled.div`
    ${body2}
    grid-area: label;

    ${forIE`
        -ms-grid-row: 2;
        -ms-grid-row-span: 2;
        -ms-grid-column: 1;
    `}
`;

const StyledContainer = styled.div<{ contentHeight: string; isOpen: boolean }>`
    background: ${({ theme }) => theme.colors.white};
    height: auto;
    max-height: 85vh;
    width: 100%;
    transform: translateY(500px);
    ${({ isOpen, contentHeight }) => css`
        width: 100%;
        position: fixed;
        z-index: ${({ theme }) => theme.zIndexPopUpOverlay + 1};
        bottom: 0;
        transition: ease-in-out 0.3s;
        transform: ${isOpen ? 'translateY(0)' : `translateY(${contentHeight})`};
        ${forDesktop`    
            width: 415px;
            height: 100vh;
            right: 0;
            bottom: unset;
            max-height: 100vh;
            transform: ${isOpen ? 'translateX(0)' : 'translateX(415px)'};
        `}
    `}
`;

const StyledList = styled.ul<{ contentHeight: string }>`
    max-height: 500px;
    ${forDesktop`
        max-height: calc(100vh - ${SIZE_OVERLAY_HEADER_HEIGHT}px);
        margin: 0;
    `}
    overflow-y: auto;
`;

const StyledListItem = styled.li<{ selected?: boolean }>`
    display: flex;
    height: 77px;
    cursor: pointer;
    align-items: center;
    background: ${({ selected, theme }) => (selected ? theme.colors.surfaceTurquoise : '')};
    ${({ selected, theme }) => css`
        ${forDesktop`
            :hover {
                background: ${selected ? '' : theme.colors.surfaceTurquoise};
            }
        `}
    `}
`;

type Props = {
    isOpen?: boolean;
    dismissFunction: () => void;
};

const SizeSelectorContent: React.FC<Props> = ({ dismissFunction, isOpen = false }) => {
    const t = useBellaTranslator();
    const bellaPush = useBellaPush();
    const [pdpUrlState] = usePdpUrlState();
    const scrollLockRef = useScrollLockRef({ reserveScrollBarGap: true }, isOpen);
    const [contentHeight, setContentHeight] = useState('auto');
    const { sizeOptions } = useSelectProductSize(false);

    const discountColor = useBellaSelector(state => state.configurations.specialSales.strikeThroughColor);

    useEffect(() => {
        if (sizeOptions.length > 1) {
            // We need a specific height to handle the animation, hence this calculation;
            setContentHeight(`${sizeOptions.length * SIZE_ITEM_HEIGHT + SIZE_OVERLAY_HEADER_HEIGHT}px`);
        }
    }, [sizeOptions]);

    return (
        <StyledContainer isOpen={isOpen} contentHeight={contentHeight} ref={scrollLockRef}>
            <StyledHeader
                title={t('Size')}
                closeBtnPosition={Direction.Right}
                titlePosition={TitlePosition.LEFT}
                showBorder
                dense
                onCloseClick={dismissFunction}
            />
            <StyledList contentHeight={contentHeight}>
                {sizeOptions.map(({ isSelected, disabled, price, originalPrice, label, value }: SizeOptions) => {
                    const sizeUrl = convertPdpStateToUrl({
                        ...pdpUrlState,
                        activeSimpleSku: value,
                    });

                    const handleItemClick = () => {
                        dismissFunction();
                        setTimeout(() => {
                            bellaPush(sizeUrl);
                        }, 200); // The overlay animation take 300ms this is slightly faster
                    };

                    return (
                        <StyledListItem
                            key={value}
                            data-testid={BellaTestIds.pdpSizeSelectorItem}
                            selected={isSelected}
                        >
                            <SizeItem disabled={disabled} onClick={handleItemClick}>
                                <StyledSizeNameContainer>
                                    <div>{label}</div>
                                </StyledSizeNameContainer>
                                <StyledPriceContainer
                                    price={price}
                                    originalPrice={originalPrice}
                                    discountColor={discountColor}
                                    sizeSelector
                                    testId={BellaTestIds.pdpSizeSelectorItemPrice}
                                />
                            </SizeItem>
                        </StyledListItem>
                    );
                })}
            </StyledList>
        </StyledContainer>
    );
};

export default SizeSelectorContent;
